import type { AggregatedData } from '#calculator/aggregateData'

/**
 * What the spend-less lever cannot move, in dollars.
 *
 * Defined by subtraction rather than by listing the engine's charges, so a kind
 * of generated expense nobody has thought of yet lands on the right side of the
 * line without this file changing. Property tax and loan payments are today's
 * two.
 */
export const nonAdjustableExpenses = (data: AggregatedData): number =>
  data.expenses.totalValue - data.adjustableExpenses.totalValue

/**
 * The factor to scale the user's own expense records by, for a target total.
 *
 * The lever shows the whole expense line and moves only part of it, so the two
 * are not in proportion: against $5k adjustable and $3k fixed, a drag from $8k
 * to $6k is a $2k cut that has to come entirely out of the $5k — a factor of
 * 0.6, where the displayed ratio would say 0.75.
 *
 * Returns 1 when nothing is adjustable, which keeps the projection unchanged
 * rather than dividing by zero. A lever with nothing to move should not be on
 * screen at all; `useAdjustments` is what decides that.
 */
export const expenseMultiplierFor = (
  targetExpenses: number,
  totalExpenses: number,
  nonAdjustable: number
): number => {
  const adjustable = totalExpenses - nonAdjustable
  if (adjustable <= 0) return 1
  return Math.max(0, targetExpenses - nonAdjustable) / adjustable
}

/**
 * The lowest ratio the lever may hold — the one whose value is the floor.
 *
 * Needed because the floor moves: a ratio held from before a loan was added is
 * still a valid number, and now points below what the lever can reach.
 */
export const minExpenseRatio = (
  totalExpenses: number,
  nonAdjustable: number
): number => {
  if (totalExpenses <= 0) return 0
  return Math.min(0, (nonAdjustable - totalExpenses) / totalExpenses)
}
